import {
  IonAccordion,
  IonAccordionGroup,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonRadioGroup,
  IonRow,
  IonRadio,
  IonInput,
  IonText,
  IonTitle,
  IonToolbar,
  IonAlert,
} from "@ionic/react";
import { closeOutline, remove, add } from "ionicons/icons";
import React, { useState, useMemo } from "react";
import "./filterBlocks.css";
import {
  STRATEGY_TYPE_MAP,
  CREATE_BLOCK_FEED_FILTER_ACTION,
  UPDATE_BLOCK_FEED_FILTER_ACTION,
} from "./constants";
import {
  assetClassAndProductFilterOptions,
  blockContents,
  exchanges,
} from "./filterOptionsNew";
import { SubOption } from "./filterOptionsNew";
import { BlockFeedFilter } from "./type";
import { send } from "../../utils/sockets/asyncSocket";
import config from "../helpers/socket/config";
import { v4 as uuidv4 } from "uuid";
import firebaseEventConfig from "../../utils/firebaseAnalytics/firebaseEventConfig";
import { CMEDfirebaseLogEvent } from "../../utils/firebaseAnalytics/firebaseUtils";

interface FilterModalBlockTradesProps {
  onclose: () => void;
  assetClassActiveFilters: Record<string, Set<string>>;
  setAssetClassActiveFilters: (filters: Record<string, Set<string>>) => void;
  blockContentActiveFilter: Set<string>;
  SetBlockContentActiveFilter: React.Dispatch<
    React.SetStateAction<Set<string>>
  >;
  exchangesFilter: Set<string>;
  setExchangesFilter: React.Dispatch<React.SetStateAction<Set<string>>>;
  selectedDate: string;
  setSelectedDate: any;
  activeSavedFilterId: number | null;
  setActiveSavedFilterId: React.Dispatch<React.SetStateAction<number | null>>;
  savedFilters: BlockFeedFilter[];
  setSavedFilters: React.Dispatch<React.SetStateAction<BlockFeedFilter[]>>;
  currentSelectedProfile: number | undefined;
  updateActiveFilter: (filterId: number, isActive?: boolean) => void;
}

const BLOCK_FEED_FILTER_GROUPS = [
  "assetClasses",
  "blockContents",
  "exchanges",
  "tradeDate",
];

export const FilterModalBlockTrades = ({
  onclose,
  assetClassActiveFilters,
  setAssetClassActiveFilters,
  blockContentActiveFilter,
  SetBlockContentActiveFilter,
  exchangesFilter,
  setExchangesFilter,
  selectedDate,
  setSelectedDate,
  activeSavedFilterId,
  setActiveSavedFilterId,
  savedFilters,
  setSavedFilters,
  currentSelectedProfile,
  updateActiveFilter,
}: FilterModalBlockTradesProps) => {
  const [assetSelectedFilter, setAssetSelectedFilter] = useState<
    Record<string, Set<string>>
  >({ ...assetClassActiveFilters });
  const [localBlockContentsFilter, setLocalBlockContentsFilter] = useState<
    Set<string>
  >(blockContentActiveFilter);
  const [localExchangesFilter, setLocalExchangesFilter] =
    useState<Set<string>>(exchangesFilter);
  const [localSelectedDate, setLocalSelectedDate] =
    useState<string>(selectedDate);

  const [expandedAccordianItem, setExpandedAccordianItem] =
    useState<String | null>(null);

  const [isSaveAlertOpen, setIsSaveAlertOpen] = useState(false);
  const [isOverwriteAlertOpen, setIsOverwriteAlertOpen] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [isDuplicateFilterName, setIsDuplicateFilterName] = useState(false);
  const [isFilterNameEmpty, setIsFilterNameEmpty] = useState(false);
  const [saveAlertKey, setSaveAlertKey] = useState(0);

  const dateOptions = useMemo(() => {
    const days: { label: string; value: string }[] = [];
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    days.push({
      label: "Today",
      value: "topday",
    });

    let i = 1;
    while (days.length < 5) {
      const date = new Date();
      date.setDate(date.getDate() - i);

      i++;
      // Skip weekends (0 = Sunday, 6 = Saturday)
      const dayOfWeek = date.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        continue;
      }

      const month = date.getMonth() + 1;
      const day = date.getDate();
      const year = date.getFullYear();

      const formattedValue = `${month}-${day}-${year}`;
      const dateDisplayLabel = date.toLocaleDateString("en-US", options);

      days.push({
        label: dateDisplayLabel,
        value: formattedValue,
      });
    }

    return days;
  }, []);

  const toggleFilter = (group: string, item?: SubOption) => {
    setAssetSelectedFilter((prev) => {
      const newFilter = { ...prev };
      if (!item) {
        if (
          newFilter[group] != null &&
          newFilter[group].size ===
            assetClassAndProductFilterOptions.find(
              (assetclassOption) => assetclassOption.value === group,
            )?.options.length
        ) {
          delete newFilter[group];
        } else {
          newFilter[group] = new Set([
            ...(assetClassAndProductFilterOptions
              .find((assetclassOption) => assetclassOption.value === group)
              ?.options?.map((option) => option.value) ?? []),
          ]);
        }
      } else {
        const groupSet = newFilter[group] || new Set();
        if (groupSet.has(item.value)) {
          groupSet.delete(item.value);
          if (groupSet.size === 0) {
            delete newFilter[group];
          }
        } else {
          groupSet.add(item.value);
          newFilter[group] = groupSet;
        }
      }
      return newFilter;
    });
  };

  const isChecked = (group: string, item?: SubOption) => {
    if (!item) {
      return (
        assetSelectedFilter[group]?.size ===
        assetClassAndProductFilterOptions.find(
          (option) => option.value === group,
        )?.options?.length
      );
    }
    return (
      assetSelectedFilter[group] != null &&
      (assetSelectedFilter[group] as Set<string>).has(item.value)
    );
  };

  const resetFilters = () => {
    setAssetSelectedFilter({});
    setLocalBlockContentsFilter(new Set());
    setLocalExchangesFilter(new Set());
    setLocalSelectedDate(dateOptions[0]["value"]);
  };

  const applyFilters = () => {
    setAssetClassActiveFilters(assetSelectedFilter);
    SetBlockContentActiveFilter(localBlockContentsFilter);
    setExchangesFilter(localExchangesFilter);
    setSelectedDate(localSelectedDate);
    onclose();
  };

  const buildFilterPayload = () => {
    const assetClassFiltersPayload: Record<string, string[]> = {};
    Object.entries(assetSelectedFilter).forEach(([key, valueSet]) => {
      assetClassFiltersPayload[key] = Array.from(valueSet);
    });

    return {
      assetClassFilters: assetClassFiltersPayload,
      blockTypes: Array.from(localBlockContentsFilter),
      exchanges: Array.from(localExchangesFilter),
    };
  };

  const handleSaveFilterClick = () => {
    if (activeSavedFilterId) {
      setIsOverwriteAlertOpen(true);
    } else {
      setFilterName("");
      setIsDuplicateFilterName(false);
      setSaveAlertKey((prev) => prev + 1);
      setIsSaveAlertOpen(true);
    }
  };

  const saveNewFilter = (name: string) => {
    if (!name.trim()) return;
    CMEDfirebaseLogEvent(
      firebaseEventConfig.CMEDComponentNames.CMED_BLOCK_FEED,
      firebaseEventConfig.eventNames.CMED_BLOCK_FEED
        .CMED_BLOCK_FEED_SAVE_FILTER,
    );
    const payloadData = buildFilterPayload();
    const newFilter: BlockFeedFilter = {
      profileId: currentSelectedProfile,
      filterName: name,
      isActive: true,
      ...payloadData,
    };

    const request = {
      action: CREATE_BLOCK_FEED_FILTER_ACTION,
      requestID: uuidv4(),
      payload: newFilter,
      type: "createBlockFeedFilters",
    };

    send(config.socket.channels.CORE, request);
    setIsSaveAlertOpen(false);
    onclose();
  };

  const overwriteFilter = () => {
    const activeFilter = savedFilters.find((f) => f.id === activeSavedFilterId);
    if (!activeFilter) return;
    CMEDfirebaseLogEvent(
      firebaseEventConfig.CMEDComponentNames.CMED_BLOCK_FEED,
      firebaseEventConfig.eventNames.CMED_BLOCK_FEED
        .CMED_BLOCK_FEED_SAVE_FILTER,
    );
    const payloadData = buildFilterPayload();
    const updatedFilter: BlockFeedFilter = {
      id: activeFilter.id,
      profileId: currentSelectedProfile,
      filterName: activeFilter.filterName,
      isActive: true,
      ...payloadData,
    };

    const request = {
      action: UPDATE_BLOCK_FEED_FILTER_ACTION,
      requestID: uuidv4(),
      payload: updatedFilter,
      type: "createBlockFeedFilters",
    };

    send(config.socket.channels.CORE, request);

    // Optimistically update UI
    setSavedFilters(
      savedFilters.map((f) => (f.id === activeFilter.id ? updatedFilter : f)),
    );

    applyFilters();
    setIsOverwriteAlertOpen(false);
  };

  const combinedBlockContents = useMemo(() => {
    const originalBlockContents = [...blockContents];
    const strategyTypes: { id: string; optionTitle: string; keys: string[] }[] =
      [];

    Object.entries(STRATEGY_TYPE_MAP).forEach(([key, value]) => {
      const existingStrategy = strategyTypes.find(
        (item) => item.optionTitle === value,
      );

      if (existingStrategy) {
        existingStrategy.keys.push(key);
      } else {
        strategyTypes.push({ id: key, optionTitle: value, keys: [key] });
      }
    });

    strategyTypes.sort((a, b) => a.optionTitle.localeCompare(b.optionTitle));

    const mappedOriginals = originalBlockContents.map((item) => ({
      ...item,
      keys: [item.id],
    }));

    return [...mappedOriginals, ...strategyTypes];
  }, []);

  const handleBlockContentClick = (blockKeys: string[]) => {
    setLocalBlockContentsFilter((prev) => {
      const newOptions = new Set(prev);

      const isCurrentlyActive = blockKeys.some((key) => newOptions.has(key));

      blockKeys.forEach((key) => {
        if (isCurrentlyActive) {
          newOptions.delete(key);
        } else {
          newOptions.add(key);
        }
      });

      return newOptions;
    });
  };

  const isBlockContentChecked = (blockKeys: string[]) => {
    if (!localBlockContentsFilter || localBlockContentsFilter.size === 0)
      return false;
    return blockKeys.every((key) => localBlockContentsFilter.has(key));
  };

  return (
    <>
      <IonModal isOpen={true} onDidDismiss={onclose} className="filter-modal">
        <IonHeader className="ion-no-border">
          <IonToolbar>
            <IonTitle>
              <p className="ion-no-margin text-bold">FILTER BLOCK TRADES</p>
            </IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={onclose}>
                <IonIcon
                  icon={closeOutline}
                  color="dark"
                  slot="icon-only"
                ></IonIcon>
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <IonAccordionGroup
            expand="inset"
            id="blockFeedFilter"
            onIonChange={(e) => {
              (e.detail.value == null ||
                BLOCK_FEED_FILTER_GROUPS.includes(e.detail.value)) &&
                setExpandedAccordianItem(e?.detail?.value);
            }}
          >
            <IonAccordion value="assetClasses">
              <IonItem slot="header">
                <IonLabel>Asset Classes & Product Groups</IonLabel>
                <IonIcon
                  slot="end"
                  className="ion-accordion-toggle-icon"
                  icon={expandedAccordianItem === "assetClasses" ? remove : add}
                />
              </IonItem>
              <div slot="content">
                {assetClassAndProductFilterOptions.map((filterItem) => (
                  <IonList key={filterItem.optionTitle}>
                    <IonItem>
                      <IonCheckbox
                        labelPlacement="end"
                        mode="md"
                        justify="start"
                        checked={isChecked(filterItem.value)}
                        onIonChange={() => toggleFilter(filterItem.value)}
                        style={{
                          "--gap": "8px",
                          "--padding-start": "0",
                        }}
                      >
                        {filterItem.optionTitle}
                      </IonCheckbox>
                    </IonItem>
                    {filterItem.options.map((childOption, index) => {
                      return (
                        <IonItem key={index} className="sub-category">
                          <IonCheckbox
                            labelPlacement="end"
                            mode="md"
                            justify="start"
                            checked={isChecked(filterItem.value, childOption)}
                            onIonChange={() =>
                              toggleFilter(filterItem.value, childOption)
                            }
                            style={{
                              "--gap": "8px",
                              "--padding-start": "0",
                            }}
                          >
                            {childOption.title}
                          </IonCheckbox>
                        </IonItem>
                      );
                    })}
                  </IonList>
                ))}
              </div>
            </IonAccordion>

            <IonAccordion value="blockContents">
              <IonItem slot="header">
                <IonLabel>Type</IonLabel>
                <IonIcon
                  slot="end"
                  className="ion-accordion-toggle-icon"
                  icon={
                    expandedAccordianItem === "blockContents" ? remove : add
                  }
                />
              </IonItem>
              <div
                slot="content"
                style={{ maxHeight: "300px", overflowY: "auto" }}
              >
                <IonGrid className="ion-no-padding">
                  <IonRow
                    style={{
                      "--min-height": "40px",
                      background: "var(--ion-item-background)",
                    }}
                    className="ion-align-items-stretch"
                  >
                    {combinedBlockContents.map((blockItem, index) => (
                      <IonCol
                        size="6"
                        key={blockItem.id || index}
                        style={{
                          display: "flex",
                          alignItems: "stretch",
                          cursor: "pointer",
                        }}
                        onClick={() => handleBlockContentClick(blockItem.keys)}
                      >
                        <IonItem
                          lines="none"
                          style={{
                            height: "100%",
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            pointerEvents: "none",
                          }}
                        >
                          <IonCheckbox
                            labelPlacement="end"
                            mode="md"
                            justify="start"
                            checked={isBlockContentChecked(blockItem.keys)}
                            style={{
                              "--gap": "8px",
                              "--padding-start": "0",
                            }}
                          >
                            <div style={{ whiteSpace: "normal" }}>
                              {blockItem.optionTitle}
                            </div>
                          </IonCheckbox>
                        </IonItem>
                      </IonCol>
                    ))}
                  </IonRow>
                </IonGrid>
              </div>
            </IonAccordion>

            <IonAccordion value="exchanges">
              <IonItem slot="header">
                <IonLabel>Exchange</IonLabel>
                <IonIcon
                  slot="end"
                  className="ion-accordion-toggle-icon"
                  icon={expandedAccordianItem === "exchanges" ? remove : add}
                />
              </IonItem>
              <div slot="content">
                {exchanges.map((filterItem) => (
                  <IonList key={filterItem.id}>
                    <IonItem>
                      <IonCheckbox
                        labelPlacement="end"
                        mode="md"
                        justify="start"
                        checked={localExchangesFilter?.has(filterItem.id)}
                        onIonChange={() =>
                          localExchangesFilter?.has(filterItem.id)
                            ? setLocalExchangesFilter((prev) => {
                                const newOptions = new Set(prev);
                                newOptions.delete(filterItem.id);
                                return newOptions;
                              })
                            : setLocalExchangesFilter((prev) => {
                                const newOptions = new Set(prev);
                                newOptions.add(filterItem.id);
                                return newOptions;
                              })
                        }
                        style={{
                          "--gap": "8px",
                          "--padding-start": "0",
                        }}
                      >
                        {filterItem.optionTitle}
                      </IonCheckbox>
                    </IonItem>
                  </IonList>
                ))}
              </div>
            </IonAccordion>
            <IonAccordion value="tradeDate">
              <IonItem slot="header">
                <IonLabel>Trade Date</IonLabel>
                <IonIcon
                  slot="end"
                  className="ion-accordion-toggle-icon"
                  icon={expandedAccordianItem === "tradeDate" ? remove : add}
                />
              </IonItem>
              <div slot="content">
                <IonRadioGroup
                  value={localSelectedDate}
                  onIonChange={(e) => setLocalSelectedDate(e.detail.value)}
                >
                  {dateOptions.map(({ label, value }) => (
                    <IonList key={value}>
                      <IonItem>
                        <IonRadio
                          labelPlacement="end"
                          mode="md"
                          justify="start"
                          value={value}
                          style={{
                            "--gap": "8px",
                            "--padding-start": "0",
                          }}
                        >
                          {label}
                        </IonRadio>
                      </IonItem>
                    </IonList>
                  ))}
                </IonRadioGroup>
              </div>
            </IonAccordion>
          </IonAccordionGroup>
        </IonContent>
        <IonFooter
          className="ion-no-padding ion-no-border"
          style={{ marginBottom: "20px" }}
        >
          <IonGrid className="ion-padding">
            <IonRow>
              <IonCol size="12">
                <IonButton
                  className="custom_cmed_mobile_button"
                  onClick={handleSaveFilterClick}
                  color="tertiary"
                  expand="block"
                  fill="solid"
                  style={{ marginBottom: "10px" }}
                >
                  SAVE FILTER
                </IonButton>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol size="6">
                <IonButton
                  className="custom_cmed_mobile_button"
                  onClick={resetFilters}
                  color="medium"
                  expand="block"
                  fill="solid"
                >
                  RESET FILTERS
                </IonButton>
              </IonCol>
              <IonCol size="6">
                <IonButton
                  className="custom_cmed_mobile_button"
                  onClick={() => {
                    activeSavedFilterId != null &&
                      updateActiveFilter(activeSavedFilterId, false);
                    setActiveSavedFilterId(null);
                    applyFilters();
                  }}
                  expand="block"
                  fill="solid"
                  color="primary"
                >
                  APPLY FILTERS
                </IonButton>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonFooter>
      </IonModal>

      {/* Save New Filter Modal */}
      <IonModal
        key={`save-modal-${saveAlertKey}`}
        isOpen={isSaveAlertOpen}
        onDidDismiss={() => {
          setIsSaveAlertOpen(false);
          setFilterName("");
          setIsDuplicateFilterName(false);
          setIsFilterNameEmpty(false);
        }}
        style={{
          "--width": "calc(100% - 40px)",
          "--max-width": "400px",
          "--height": "auto",
          "--box-shadow": "0px 4px 20px rgba(0, 0, 0, 0.4)",
          "--backdrop-opacity": "0.6",
          "--border-color": "var(--ion-color-step-150, rgba(0, 0, 0, 0.1))",
          "--border-width": "1px",
          "--border-style": "solid",
        }}
      >
        <IonHeader className="ion-no-border">
          <IonToolbar>
            <IonTitle>Save Filter</IonTitle>
          </IonToolbar>
        </IonHeader>
        <div className="ion-padding">
          <IonItem
            className={
              isDuplicateFilterName || isFilterNameEmpty
                ? "ion-invalid ion-touched"
                : ""
            }
            style={
              isDuplicateFilterName || isFilterNameEmpty
                ? {
                    "--border-color": "var(--ion-color-danger, red)",
                    "--highlight-color-invalid": "var(--ion-color-danger, red)",
                  }
                : {}
            }
          >
            <IonInput
              type="text"
              placeholder="Enter filter name"
              value={filterName}
              onIonInput={(e) => {
                setFilterName((e.detail.value as string) || "");
                setIsFilterNameEmpty(false);
                setIsDuplicateFilterName(false);
              }}
            />
          </IonItem>
          {(isDuplicateFilterName || isFilterNameEmpty) && (
            <IonText color="danger">
              <p
                className="ion-padding-start ion-no-margin"
                style={{ fontSize: "14px", marginTop: "8px" }}
              >
                {isDuplicateFilterName
                  ? "Filter name already exists."
                  : "Filter name is required."}
              </p>
            </IonText>
          )}
        </div>
        <IonFooter className="ion-no-border">
          <IonToolbar>
            <IonButtons slot="end" className="ion-padding-horizontal">
              <IonButton
                color="medium"
                onClick={() => {
                  setIsDuplicateFilterName(false);
                  setFilterName("");
                  setIsSaveAlertOpen(false);
                }}
              >
                Cancel
              </IonButton>
              <IonButton
                color="primary"
                onClick={() => {
                  if (!filterName || filterName.trim() === "") {
                    setIsFilterNameEmpty(true);
                    return;
                  } else if (
                    (
                      savedFilters.map((filter) =>
                        filter.filterName.toLowerCase(),
                      ) ?? []
                    ).includes(
                      filterName.replace(/\s+/g, " ").trim().toLowerCase(),
                    )
                  ) {
                    setIsDuplicateFilterName(true);
                    return;
                  }
                  setIsFilterNameEmpty(false);
                  setIsDuplicateFilterName(false);
                  saveNewFilter(
                    filterName.replace(/\s+/g, " ").trim().toLowerCase(),
                  );
                }}
              >
                Save
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
      </IonModal>

      {/* Overwrite or Save as New Alert */}
      <IonAlert
        isOpen={isOverwriteAlertOpen}
        onDidDismiss={() => setIsOverwriteAlertOpen(false)}
        header="Save Filter Options"
        message="You are currently editing a saved filter. Would you like to overwrite it, or save this as a new filter?"
        buttons={[
          {
            text: "Save as New",
            handler: () => {
              setIsOverwriteAlertOpen(false);
              setFilterName("");
              setIsDuplicateFilterName(false);
              setSaveAlertKey((prev) => prev + 1);
              setIsSaveAlertOpen(true);
            },
          },
          {
            text: "Overwrite",
            handler: () => {
              overwriteFilter();
            },
          },
        ]}
      />
    </>
  );
};
